export const HARDWARE_COST_BANDS = [
  "Lowest",
  "Low cost",
  "Mid cost",
  "High cost",
  "Most expensive",
] as const;

export type HardwareCostBand = (typeof HARDWARE_COST_BANDS)[number];

const UNKNOWN_COST_BAND: HardwareCostBand = "Mid cost";

function knownHardwareCost(value: unknown): number | null {
  const cost = Number(value);
  return Number.isFinite(cost) && cost > 0 ? cost : null;
}

/**
 * Assigns customer-facing cost bands from the relative rank of known prices.
 *
 * Exact prices remain available to callers for sorting, reservation, and
 * checkout, but are deliberately not included in the returned labels. Equal
 * prices always receive the same band. Unique ranks use
 * round(rank * 4 / (uniqueCount - 1)); a single known tier is Lowest.
 * Missing/non-positive prices receive the neutral middle band rather than
 * being presented as cheapest or dearest.
 */
export function assignHardwareCostBands<T>(
  items: readonly T[],
  getCost: (item: T) => unknown,
): HardwareCostBand[] {
  const costs = items.map((item) => knownHardwareCost(getCost(item)));
  const uniqueCosts = [...new Set(costs.filter((cost): cost is number => cost !== null))]
    .sort((left, right) => left - right);

  if (uniqueCosts.length === 0) {
    return items.map(() => UNKNOWN_COST_BAND);
  }

  const bandByCost = new Map<number, HardwareCostBand>();
  uniqueCosts.forEach((cost, index) => {
    const bandIndex = uniqueCosts.length === 1
      ? 0
      : Math.round((index * (HARDWARE_COST_BANDS.length - 1)) / (uniqueCosts.length - 1));
    bandByCost.set(cost, HARDWARE_COST_BANDS[bandIndex] ?? UNKNOWN_COST_BAND);
  });

  return costs.map((cost) => cost === null ? UNKNOWN_COST_BAND : bandByCost.get(cost) ?? UNKNOWN_COST_BAND);
}

export function hardwareCostBandLabel(band: HardwareCostBand): `(${HardwareCostBand})` {
  return `(${band})`;
}
